import { useState, useEffect, useCallback } from 'react';
import { request } from '../../../lib/api';

export interface CatalogOption {
  id: number;
  name: string;
  code?: string;
}

export interface Province {
  id: number;
  name: string;
}

export interface Municipality {
  id: number;
  name: string;
  province_id: number;
}

export interface District {
  id: number;
  name: string;
  municipality_id: number;
}

export interface InsurancePlan {
  id: number;
  name: string;
  company: {
    id: number;
    name: string;
  };
}

export const usePatientCatalogs = () => {
  const [identificationTypes, setIdentificationTypes] = useState<CatalogOption[]>([]);
  const [nationalities, setNationalities] = useState<CatalogOption[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [insurancePlans, setInsurancePlans] = useState<InsurancePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial catalogs
  const loadCatalogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        identificationTypesData,
        nationalitiesData,
        provincesData,
        municipalitiesData,
        districtsData,
        insurancePlansData,
      ] = await Promise.all([
        request('/api/v1/settings/identification_types'),
        request('/api/v1/settings/nationalities'),
        request('/api/v1/settings/provinces'),
        request('/api/v1/settings/municipalities'),
        request('/api/v1/settings/municipal_districts'),
        request('/api/v1/settings/insurance_plans'),
      ]);

      setIdentificationTypes(identificationTypesData.data || []);
      setNationalities(nationalitiesData.data || []);
      setProvinces(provincesData.data || []);
      setMunicipalities(municipalitiesData.data || []);
      setDistricts(districtsData.data || []);
      setInsurancePlans(insurancePlansData.data || []);
    } catch (err) {
      console.error('Error loading catalogs:', err);
      setError('Error loading catalogs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  // Filter municipalities by province
  const getMunicipalitiesByProvince = useCallback((provinceId: number) => {
    return municipalities.filter(m => m.province_id === provinceId);
  }, [municipalities]);

  // Filter districts by municipality
  const getDistrictsByMunicipality = useCallback((municipalityId: number) => {
    return districts.filter(d => d.municipality_id === municipalityId);
  }, [districts]);

  return {
    identificationTypes,
    nationalities,
    provinces,
    municipalities,
    districts,
    insurancePlans,
    loading,
    error,
    reloadCatalogs: loadCatalogs,
    getMunicipalitiesByProvince,
    getDistrictsByMunicipality,
  };
};
